/** @file followAngles: an entry's drawn angles carried onto another sector table, tile by nearest tile. */
import { sectorCentre } from '@zilo/pose3d-format/tables/sectorCentre';
import { tileCountOf } from '@zilo/pose3d-format/tables/tileCountOf';
import type { FacetEntry, SectorTable } from '@zilo/pose3d-format/types/facet';
import type { DrawnAngles } from '../types/puppetInputs';
import { compact } from '../validate/compact';

/** Nominal azimuth tile `tile` is drawn at: its first unflipped sector's centre; 0 for a one-sector table. */
function nominalAzimuth(table: SectorTable, tile: number): number {
  if (table.thresholds.length === 1) return 0;
  const plain = table.tile.findIndex((t, s) => t === tile && table.flip[s] !== true);
  return sectorCentre(table, plain >= 0 ? plain : table.tile.indexOf(tile));
}

/** Angular distance in degrees, in [0, 180]. */
const gap = (a: number, b: number): number => Math.abs(((((a - b) % 360) + 540) % 360) - 180);

/**
 * The drawn angles `entry` needs on `to` (it uses `from` now): each new tile takes the drawn azimuth and drawn
 * screen angle of the old tile drawn nearest its nominal azimuth (ties: the lower tile). Arrays the entry lacks stay
 * absent; the result feeds `retargetEntry`.
 */
export function followAngles(entry: FacetEntry, from: SectorTable, to: SectorTable): DrawnAngles {
  const old = Array.from({ length: tileCountOf(from) }, (_, j) => entry.drawnAzimuths?.[j] ?? nominalAzimuth(from, j));
  const pick = Array.from({ length: tileCountOf(to) }, (_, t) => {
    const want = nominalAzimuth(to, t);
    return old.reduce((best, a, j) => (gap(a, want) < gap(old[best] ?? 0, want) ? j : best), 0);
  });
  const az = entry.drawnAzimuths;
  const screen = entry.drawnScreenAngles;
  return compact({
    drawnAzimuths: az === undefined ? undefined : pick.map((j) => az[j] ?? 0),
    drawnScreenAngles: screen === undefined ? undefined : pick.map((j) => screen[j] ?? 0),
  });
}
